import { useMemo } from "react";
import "./MindMappingReportBSixthSection.css";

const QTag = ({ q, a, impacts }) => (
  <div style={{display:'flex',flexWrap:'wrap',gap:'5px 10px',alignItems:'center',background:'#f0f4ff',border:'1px solid #c7d7fa',borderRadius:'6px',padding:'7px 12px',marginBottom:'12px',fontSize:'12px',lineHeight:1.5}}>
    <span style={{background:'#3b5bdb',color:'#fff',fontWeight:700,padding:'2px 8px',borderRadius:4,fontSize:'10px',whiteSpace:'nowrap'}}>📋 FORM QUESTION</span>
    <span style={{color:'#1e3a8a',fontStyle:'italic'}}>"{q}"</span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#047857',fontWeight:700}}>You answered: <strong>{a}</strong></span>
    <span style={{color:'#6b7280',fontSize:'11px'}}>→</span>
    <span style={{color:'#7c3aed',fontSize:'11px'}}>📊 Impacts: {impacts}</span>
  </div>
);

/* ── MBTI letter map ──────────────────────────────────────────── */
const LETTER = {
  Extravert: "E", Introvert: "I",
  Thinking:  "T", Feeling:   "F",
  Judging:   "J", Perceiving:"P",
  Intuition: "N", Sensing:   "S",
};

/* ── MBTI archetype profiles ─────────────────────────────────── */
const MBTI_PROFILES = {
  INTJ:{ name:"The Architect",    fit:"Deep tech, platform businesses, long-cycle B2B SaaS" },
  INTP:{ name:"The Logician",     fit:"Research products, developer tools, AI/ML startups" },
  ENTJ:{ name:"The Commander",    fit:"Operations-heavy startups, enterprise SaaS, market leadership" },
  ENTP:{ name:"The Debater",      fit:"Consumer internet, marketplace disruption, creative tech" },
  INFJ:{ name:"The Advocate",     fit:"Social impact, EdTech, mental health, mission-driven ventures" },
  INFP:{ name:"The Mediator",     fit:"Creative platforms, wellness brands, community products" },
  ENFJ:{ name:"The Protagonist",  fit:"Consumer brands, community platforms, social startups" },
  ENFP:{ name:"The Campaigner",   fit:"Consumer apps, media, creator economy, viral products" },
  ISTJ:{ name:"The Logistician",  fit:"Fintech, compliance-tech, supply chain, B2B ops tools" },
  ISFJ:{ name:"The Defender",     fit:"Healthcare support, childcare tech, education services" },
  ESTJ:{ name:"The Executive",    fit:"Enterprise ops, manufacturing tech, process automation" },
  ESFJ:{ name:"The Consul",       fit:"Retail tech, D2C services, healthcare, community ventures" },
  ISTP:{ name:"The Virtuoso",     fit:"Hardware, manufacturing, deep engineering, IoT" },
  ISFP:{ name:"The Adventurer",   fit:"D2C lifestyle brands, artisan tech, food & wellness" },
  ESTP:{ name:"The Entrepreneur", fit:"Sales-led growth, high-velocity consumer ventures" },
  ESFP:{ name:"The Entertainer",  fit:"Events tech, entertainment, experiential consumer products" },
};

const MindMappingReportBSixthSection = ({ res, toggle }) => {
  const traits = useMemo(() => ({
    intro_extro:    res?.trait_intro_extro    || "Introvert",
    think_feel:     res?.trait_think_feel     || "Thinking",
    judge_perceive: res?.trait_judge_perceive || "Judging",
    sense_intuit:   res?.trait_sense_intuit   || "Sensing",
  }), [res]);

  const traitMapping = {
    "Introvert": {
      adv: "Deep thinking, focus, strong product vision, thorough market research",
      dis: "Less visible, weaker public pitching presence, slower network building",
      investor: "⚠️ Neutral (needs strong storytelling)",
      bestFit: "Product-led startups, DeepTech, SaaS",
      advice: "Introverted founders produce deeper, research-backed products — channel this into thorough market research, product roadmaps, and written content marketing. Compensate for pitching gaps with a strong co-founder or advisor who can be the public face."
    },
    "Extravert": {
      adv: "Strong communication, fast networking, natural sales instinct, high visibility",
      dis: "Risk of over-promising, may underinvest in deep product work",
      investor: "✅ Positive in pitches",
      bestFit: "Marketplaces, D2C, Sales-driven startups",
      advice: "Extroverted founders build investor relationships faster — leverage networking events, demo days, and warm introductions to accelerate fundraising timelines. Balance your outward energy with structured product development cycles."
    },
    "Thinking": {
      adv: "Logical decisions, data-driven, strong unit economics focus, systems thinking",
      dis: "Can ignore user emotions, may under-invest in brand storytelling",
      investor: "🏆 Preferred for scale",
      bestFit: "B2B SaaS, FinTech, Ops-heavy startups",
      advice: "Analytical decision-makers excel at unit economics and systems design — use this strength when pitching operational efficiency and scalability to investors. Back every claim with data: CAC, LTV, payback period, and gross margin."
    },
    "Feeling": {
      adv: "Deep empathy, strong customer understanding, high team morale, mission clarity",
      dis: "Emotional bias in decisions, difficulty cutting loss-making product lines",
      investor: "⚠️ Neutral—Positive",
      bestFit: "HealthTech, EdTech, Community platforms",
      advice: "Empathy-driven founders build higher customer retention and NPS — highlight user-centricity and community loyalty as a competitive moat in investor conversations. Pair with a Thinking-type co-founder for financial discipline."
    },
    "Judging": {
      adv: "Strong planning, execution discipline, milestone-driven, organized operations",
      dis: "Can be rigid, may resist pivoting when needed",
      investor: "🏆 Highly preferred",
      bestFit: "Enterprise SaaS, Regulated sectors, Deep-cycle B2B",
      advice: "Structured executors close milestones on time — use this trait to demonstrate strong project management and execution credibility in your pitch deck. Show a detailed 12-month milestone roadmap to investors; it signals founder maturity."
    },
    "Perceiving": {
      adv: "Flexibility, adaptability, pivot-ready, absorbs market feedback quickly",
      dis: "Lack of structure, can struggle with consistent execution and meeting deadlines",
      investor: "⚠️ Neutral (needs ops co-founder)",
      bestFit: "Early-stage, experimental, fast-pivot consumer startups",
      advice: "Adaptive founders absorb market feedback efficiently — position yourself as hypothesis-driven and showcase your rapid iteration capability. Hire a strong operations lead early to offset planning gaps."
    },
    "Sensing": {
      adv: "Practical, execution-focused, grounded in reality, detail-oriented implementation",
      dis: "May miss the macro vision, underweights long-term disruption potential",
      investor: "⚠️ Neutral",
      bestFit: "Operations, Manufacturing, Logistics, Compliance",
      advice: "Detail-oriented, reality-grounded founders build operationally sound startups — use your precision to validate assumptions with real-world evidence. Present investors with granular financial models and operational KPIs as proof of rigor."
    },
    "Intuition": {
      adv: "Visionary, future-focused, identifies non-obvious market opportunities",
      dis: "Risk of over-idealism, may struggle with near-term product specificity",
      investor: "🏆 Preferred for VC-scale bets",
      bestFit: "AI, Platforms, Disruptive Deep Tech",
      advice: "Vision-led founders identify market opportunities others miss — back your insight with data to bridge the gap between vision and investor conviction. Use scenario planning and large TAM estimates to anchor the vision in market reality."
    }
  };

  const mbtiCode = useMemo(() => {
    const e = LETTER[traits.intro_extro]    ?? "?";
    const s = LETTER[traits.sense_intuit]   ?? "?";
    const t = LETTER[traits.think_feel]     ?? "?";
    const j = LETTER[traits.judge_perceive] ?? "?";
    return `${e}${s}${t}${j}`;
  }, [traits]);

  const profile = MBTI_PROFILES[mbtiCode];

  const renderRow = (traitKey, label, sno) => {
    const val  = traits[traitKey];
    const data = traitMapping[val] || traitMapping["Introvert"];
    return (
      <>
        <tr>
          <td className="mind-mapping-report-b-sixth-col-sno">{sno}</td>
          <td className="mind-mapping-report-b-sixth-label">{label}</td>
          <td className="mind-mapping-report-b-sixth-user-val">{val}</td>
          <td>{data.adv}</td>
          <td>{data.dis}</td>
          <td>{data.investor}</td>
          <td className="mind-mapping-report-b-sixth-highlight">{data.bestFit}</td>
        </tr>
        <tr className="mind-mapping-report-b-sixth-advice-row">
          <td className="mind-mapping-report-b-sixth-col-sno" />
          <td colSpan={6} className="mind-mapping-report-b-sixth-advice-cell">
            <strong>💡 Strategic Advice:</strong> {data.advice}
          </td>
        </tr>
      </>
    );
  };

  return (
    <div className="mind-mapping-report-b-sixth-container">
      <div className={toggle ? "table-adjust" : "mind-mapping-report-b-sixth-final-table"}>
        <span className="mind-mapping-report-b-sixth-corner mind-mapping-report-b-sixth-corner-top-left" />
        <span className="mind-mapping-report-b-sixth-corner mind-mapping-report-b-sixth-corner-top-right" />
        <span className="mind-mapping-report-b-sixth-corner mind-mapping-report-b-sixth-corner-bottom-left" />
        <span className="mind-mapping-report-b-sixth-corner mind-mapping-report-b-sixth-corner-bottom-right" />

        <div className="mind-mapping-report-b-sixth-module-title">
          <span className="module-prefix">Module 6:</span> Personality Traits — Full Founder &amp; Investor Analysis
        </div>
        <QTag
          q="Personality Assessment — 4 dimensions: Energy Source, Decision Making, Execution Style, Information Processing"
          a={`${traits.intro_extro} · ${traits.think_feel} · ${traits.judge_perceive} · ${traits.sense_intuit}`}
          impacts="Founder archetype, pitch style, team composition needs, GTM strategy, and investor chemistry"
        />
        <div className="mind-mapping-report-b-sixth-table-scroll-wrapper">
          <table className="mind-mapping-report-b-sixth-inner-table">
            <thead>
              <tr>
                <th className="mind-mapping-report-b-sixth-col-sno">S.NO</th>
                <th>Dimension</th>
                <th>Your Trait</th>
                <th>Advantages</th>
                <th>Disadvantages</th>
                <th>Investor Perspective</th>
                <th>Best-Fit Startup Types</th>
              </tr>
            </thead>
            <tbody>
              {renderRow("intro_extro",    "Introvert vs Extravert", "6 A")}
              {renderRow("think_feel",     "Thinking vs Feeling",    "6 B")}
              {renderRow("judge_perceive", "Judging vs Perceiving",  "6 C")}
              {renderRow("sense_intuit",   "Sensing vs Intuition",   "6 D")}

              {/* Synthesised MBTI row — exclusive to Report A+ */}
              <>
                <tr className="mbti-synthesis-row">
                  <td className="mind-mapping-report-b-sixth-col-sno">6 E</td>
                  <td className="mind-mapping-report-b-sixth-label"><strong>Founder Archetype (MBTI)</strong></td>
                  <td className="mind-mapping-report-b-sixth-user-val">
                    <strong>{mbtiCode}{profile ? ` — ${profile.name}` : ""}</strong>
                  </td>
                  <td colSpan="2">
                    Combined MBTI profile synthesised from all four trait dimensions above
                  </td>
                  <td>
                    {mbtiCode.startsWith("E")
                      ? "✅ High visibility — strong pitch presence"
                      : "⚠️ Depth-driven — invest in written content & demos"}
                  </td>
                  <td className="mind-mapping-report-b-sixth-highlight">
                    {profile?.fit ?? "Versatile — review trait rows above for specific guidance"}
                  </td>
                </tr>
                <tr className="mind-mapping-report-b-sixth-advice-row">
                  <td className="mind-mapping-report-b-sixth-col-sno" />
                  <td colSpan={6} className="mind-mapping-report-b-sixth-advice-cell">
                    <strong>💡 Strategic Advice:</strong>{" "}
                    {profile
                      ? `As ${profile.name}, you are best suited for ${profile.fit}. Build your pitch narrative around your archetype's natural strengths and actively compensate for its blind spots by choosing complementary co-founders.`
                      : "Study your individual trait rows above to build a composite founder development plan."}
                  </td>
                </tr>
              </>

              {/* Sector fit row */}
              <>
                <tr>
                  <td className="mind-mapping-report-b-sixth-col-sno">6 F</td>
                  <td className="mind-mapping-report-b-sixth-label">Sector Alignment</td>
                  <td className="mind-mapping-report-b-sixth-user-val">
                    {res?.sector ? `${res.sector} (${res.sector_type ?? "Tertiary"})` : "Sector not specified"}
                  </td>
                  <td colSpan="2">
                    {mbtiCode.includes("T")
                      ? "Analytical strengths align with data-driven decision-making in your sector."
                      : "Empathy-led approach builds strong community and customer retention in your sector."}
                  </td>
                  <td>
                    {mbtiCode.includes("J")
                      ? "🏆 Execution discipline — investors value milestone adherence"
                      : "⚠️ Flexibility valued — pair with structured ops co-founder"}
                  </td>
                  <td className="mind-mapping-report-b-sixth-highlight">
                    {mbtiCode.startsWith("E")
                      ? `GTM-led growth preferred for ${res?.sector ?? "your sector"} — leverage networking for distribution`
                      : `Product-led growth preferred for ${res?.sector ?? "your sector"} — invest in organic, content-first acquisition`}
                  </td>
                </tr>
                <tr className="mind-mapping-report-b-sixth-advice-row">
                  <td className="mind-mapping-report-b-sixth-col-sno" />
                  <td colSpan={6} className="mind-mapping-report-b-sixth-advice-cell">
                    <strong>💡 Strategic Advice:</strong>{" "}
                    {mbtiCode.startsWith("E")
                      ? `Your ${profile?.name ?? mbtiCode} archetype thrives in customer-facing roles. For ${res?.sector ?? "your sector"}, focus on building a sales-first culture from Day 1 — hire a strong product lead to complement your GTM energy.`
                      : `Your ${profile?.name ?? mbtiCode} archetype thrives in deep-build roles. For ${res?.sector ?? "your sector"}, prioritize product excellence and organic growth loops over aggressive outbound sales in the first 12 months.`}
                  </td>
                </tr>
              </>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MindMappingReportBSixthSection;